import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Receipt, ShoppingBag, ArrowLeft } from "@phosphor-icons/react";

function StatusBadge({ status }) {
  const map = {
    paid: "bg-[#2E523A] text-white",
    pending: "bg-[#E8B291] text-[#1A1C19]",
    initiated: "bg-[#EAEBE6] text-[#63675E]",
    failed: "bg-destructive text-white",
    expired: "bg-[#EAEBE6] text-[#63675E]",
  };
  return (
    <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wide ${map[status] || map.initiated}`} data-testid="order-status">
      {status}
    </span>
  );
}

export default function Orders() {
  const [orders, setOrders] = useState(null);

  useEffect(() => {
    api.get("/orders").then((r) => setOrders(r.data)).catch(() => setOrders([]));
  }, []);

  return (
    <div className="pt-24 min-h-screen">
      <div className="max-w-4xl mx-auto px-5 sm:px-8 py-8">
        <Link to="/shop" className="flex w-fit items-center gap-1 text-sm font-medium text-[#63675E] hover:text-[#2E523A] mb-6 transition-colors duration-200" data-testid="orders-back-shop">
          <ArrowLeft size={16} /> Back to shop
        </Link>
        <span className="block text-xs tracking-[0.2em] uppercase font-bold text-[#4A6E7C]">Your purchases</span>
        <h1 className="font-display text-4xl sm:text-5xl font-bold mt-2 mb-8">My Orders</h1>

        {orders === null ? (
          <p className="text-muted-foreground py-16 text-center">Loading…</p>
        ) : orders.length === 0 ? (
          <div className="text-center py-20 border border-dashed border-[#D2D5CD] rounded-2xl" data-testid="empty-orders">
            <ShoppingBag size={48} weight="duotone" className="text-[#4A6E7C] mx-auto mb-4" />
            <p className="text-[#63675E] mb-4">You haven't placed any orders yet.</p>
            <Link to="/shop"><Button className="rounded-full bg-[#2E523A] hover:bg-[#26442f]">Browse the shop</Button></Link>
          </div>
        ) : (
          <div className="space-y-4" data-testid="orders-list">
            {orders.map((o) => (
              <div key={o.session_id} className="border border-[#D2D5CD] rounded-2xl bg-white p-6 animate-fade-up" data-testid={`order-${o.session_id}`}>
                <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
                  <div className="flex items-center gap-3">
                    <div className="h-11 w-11 rounded-xl bg-[#EAEBE6] flex items-center justify-center">
                      <Receipt size={22} weight="duotone" className="text-[#2E523A]" />
                    </div>
                    <div>
                      <p className="font-semibold text-[#1A1C19]">Order {o.session_id.slice(0, 12)}…</p>
                      <p className="text-xs text-[#63675E]">{o.created_at ? new Date(o.created_at).toLocaleString() : ""}</p>
                    </div>
                  </div>
                  <StatusBadge status={o.payment_status} />
                </div>
                <div className="divide-y divide-[#EAEBE6]">
                  {(o.items || []).map((it) => (
                    <div key={`${o.session_id}-${it.lookup_key}`} className="flex items-center gap-3 py-3">
                      {it.image && <img src={it.image} alt="" className="h-12 w-12 rounded-lg object-cover" />}
                      <span className="flex-1 text-sm font-medium text-[#1A1C19]">{it.name || it.lookup_key}</span>
                      <span className="text-sm text-[#63675E]">× {it.quantity}</span>
                    </div>
                  ))}
                </div>
                <div className="flex justify-between items-center pt-4 mt-1 border-t border-[#D2D5CD]">
                  <span className="text-sm text-[#63675E]">Total</span>
                  <span className="font-display text-xl font-bold text-[#2E523A]">${(o.amount || 0).toFixed(2)}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
